/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
import React, { useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import PropTypes from 'prop-types';
import { TouchableOpacity } from 'react-native';
import { useDispatch } from 'react-redux';

import ModalFull from '@components/modal/ModalFull';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import Button from '@components/form/Button';

import { uploadS3, downloadCsvTemplate } from '@global/helpers';
import Colors from '@global/colors';
import { isWeb, isWebPlatform } from '@global/screens';
import { showAlert } from '@/store/global';

const propTypes = {
  testID: PropTypes.string,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  templateUrl: PropTypes.string,
  onUpload: PropTypes.func,
  autoUploadS3: PropTypes.bool,
  selectedFile: PropTypes.string,
  setSelectedFile: PropTypes.func,
  name: PropTypes.string,
  validHeader: PropTypes.array,
};

const HEADER_PREVIEW_BYTES = 4096;
// Web: stream file in chunks via ReadableStream — O(chunk) memory, not O(file size).
// Falls back to FileReader.readAsText for older browsers (Safari < 14.1, etc.).
const countCsvRowsWeb = async (file) => {
  if (typeof file.stream === 'function') {
    const reader = file.stream().getReader();
    const decoder = new TextDecoder();
    let count = 0;
    let leftover = '';
    try {
      let result = await reader.read();
      while (!result.done) {
        const chunk = leftover + decoder.decode(result.value, { stream: true });
        const lines = chunk.split(/\r?\n/);
        leftover = lines.pop(); // last line may be incomplete — carry to next chunk
        count += lines.filter((l) => l.trim().length > 0).length;
        result = await reader.read();
      }
      if (leftover.trim().length > 0) {
        count++;
      }
    } finally {
      reader.releaseLock();
    }
    return Math.max(0, count - 1); // exclude header
  }
  // Fallback: FileReader (loads full file, but universally supported)
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const lines = (event.target?.result || '')
        .split(/\r?\n/)
        .filter((l) => l.trim().length > 0);
      resolve(Math.max(0, lines.length - 1));
    };
    reader.onerror = () => resolve(null);
    reader.readAsText(file);
  });
};

// Mobile (Android + iOS): full read via RNFS — works reliably with both file:// and
// content:// URIs. Positional reads (RNFS.read) don't support content URIs on Android.
const countCsvRowsMobile = async (filePath) => {
  const RNFS = require('react-native-fs');
  const content = await RNFS.readFile(filePath.uri, 'utf8');
  const lines = content.split(/\r?\n/).filter((l) => l.trim().length > 0);
  return Math.max(0, lines.length - 1); // exclude header
};

const parseCsvLine = (csvLine) => {
  const values = [];
  let currentValue = '';
  let isQuoted = false;

  for (let index = 0; index < csvLine.length; index++) {
    const currentChar = csvLine[index];
    const nextChar = csvLine[index + 1];

    if (currentChar === '"') {
      if (isQuoted && nextChar === '"') {
        currentValue += '"';
        index++;
      } else {
        isQuoted = !isQuoted;
      }
      continue;
    }

    if (currentChar === ',' && !isQuoted) {
      values.push(currentValue);
      currentValue = '';
      continue;
    }

    currentValue += currentChar;
  }

  if (isQuoted) {
    return null;
  }

  values.push(currentValue);

  return values;
};

const ModalUpload = (props) => {
  const dispatch = useDispatch();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCsvValid, setIsCsvValid] = useState(true);
  const [selectedFile, setSelectedFile] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setIsCsvValid(true);
    setSelectedFile('');
    props.onClose();
  };

  const validateCsvHeaders = (csvLine) => {
    const normalizedLine = csvLine?.replace(/^\uFEFF/, '') || '';
    const headers = parseCsvLine(normalizedLine);

    if (!headers) {
      return false;
    }

    // Length check
    if (headers.length !== props.validHeader.length) {
      return false;
    }

    // Exact content check
    for (let i = 0; i < props.validHeader.length; i++) {
      if (headers[i] !== props.validHeader[i]) {
        return false;
      }
    }

    return true;
  };

  const validateCsvWeb = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const csvContent = event.target?.result;
        const firstLine = csvContent.split(/\r?\n/)[0] || '';
        const result = validateCsvHeaders(firstLine);
        resolve(result);
      };
      reader.onerror = () => resolve(false);
      reader.readAsText(file.slice(0, HEADER_PREVIEW_BYTES));
    });
  };

  const validateCsvMobile = async (filePath) => {
    const RNFS = require('react-native-fs');
    const chunk = await RNFS.read(
      filePath.uri,
      HEADER_PREVIEW_BYTES,
      0,
      'utf8',
    );
    const firstLine = chunk.split(/\r?\n/)[0] || '';
    const result = validateCsvHeaders(firstLine);
    return result;
  };

  const handleUpload = async () => {
    setIsLoading(true);
    try {
      if (!props.validHeader?.length) {
        dispatch(
          showAlert({
            show: true,
            title: 'Invalid CSV',
            description: 'Please add valid CSV header',
            isConfirm: false,
          }),
        );

        return;
      }

      // Validate CSV File
      let isValid = false;
      if (!selectedFile?.name?.toLowerCase()?.endsWith('.csv')) {
        setIsCsvValid(false);
        return;
      }
      if (isWebPlatform) {
        isValid = await validateCsvWeb(selectedFile);
      } else {
        isValid = await validateCsvMobile(selectedFile);
      }

      if (!isValid) {
        setIsCsvValid(false);

        return;
      }

      if (props.autoUploadS3) {
        let payloadFile = selectedFile;
        if (isWebPlatform) {
          payloadFile = {
            file: selectedFile,
          };
        }
        const totalRows = isWebPlatform
          ? await countCsvRowsWeb(selectedFile)
          : await countCsvRowsMobile(selectedFile);
        const url = await uploadS3({
          file: payloadFile || {},
          isNeedMobileSupport: true,
        });
        if (!url) {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Failed to upload file. Please try again.',
              isConfirm: false,
            }),
          );
          return;
        }
        props.onUpload(url, totalRows);
      } else {
        if (isWebPlatform) {
          props.onUpload({
            file: selectedFile,
          });
        } else {
          props.onUpload(selectedFile);
        }
      }
      setSelectedFile('');
    } catch (error) {
      console.error('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const bytesToKiloBytes = (bytes) => {
    const kb = bytes / 1024;
    return kb.toFixed(2);
  };

  const WebFileDropZone = ({ selectedFile, setSelectedFile }) => {
    const inputRef = useRef(null);

    const handleDrop = (e) => {
      e.preventDefault();
      setIsCsvValid(true);
      setIsDragOver(false);
      if (e.dataTransfer.files.length && !isLoading) {
        setSelectedFile(e.dataTransfer.files[0]);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragOver(true);
    };

    const handleDragLeave = () => setIsDragOver(false);

    const handleChange = (e) => {
      setIsCsvValid(true);
      setSelectedFile(e.target.files[0]);
    };

    return (
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={styles.webDropZone(isDragOver)}
        onClick={() => {
          if (!selectedFile) {
            inputRef.current.click();
          }
        }}>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleChange}
        />

        {selectedFile ? (
          <View style={styles.fileContainer}>
            <View style={{ width: '90%' }}>
              <SociollaText>{selectedFile.name}</SociollaText>
              <SociollaText>
                {bytesToKiloBytes(selectedFile.size)} KB
              </SociollaText>
            </View>
            <View
              style={{ cursor: 'pointer', justifyContent: 'center' }}
              onClick={() => {
                setIsCsvValid(true);
                setSelectedFile('');
              }}>
              <Image
                svg={true}
                width={15}
                height={15}
                style={{ width: 15, height: 15 }}
                source={require('@assets/img/icons/trash-grey.svg')}
              />
            </View>
          </View>
        ) : (
          <View style={styles.noFileContainer}>
            <View style={{ alignItems: 'center' }}>
              <SociollaText
                style={{
                  fontWeight: 600,
                  marginBottom: 4,
                }}>
                Upload CSV file
              </SociollaText>
              <SociollaText
                style={{
                  color: '#888',
                  fontSize: 12,
                  marginBottom: 16,
                }}>
                CSV file, smaller than 1mb
              </SociollaText>
            </View>
            <Button
              title={
                <SociollaText style={{ fontSize: 14, color: Colors.white }}>
                  Upload File
                </SociollaText>
              }
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb ? 150 : '100%',
              }}
              style={{
                alignItems: 'center',
              }}
              onPress={(e) => {
                e.stopPropagation();
                inputRef.current.click();
              }}
              disabled={isLoading}
              disabledStyle={{ width: isWeb ? 150 : '100%' }}
            />
          </View>
        )}
      </div>
    );
  };

  const MobileFilePicker = ({ selectedFile, setSelectedFile }) => {
    const pickFile = async () => {
      const result = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: false,
        multiple: false,
      });
      if (!result.canceled) {
        setIsCsvValid(true);
        setSelectedFile(result.assets[0]);
      }
    };

    return (
      <View style={styles.mobileDropZone}>
        {selectedFile ? (
          <View style={styles.mobileFileContainer}>
            <View style={{ width: '90%' }}>
              <SociollaText>{selectedFile.name}</SociollaText>
              <SociollaText>
                {bytesToKiloBytes(selectedFile.size)} KB
              </SociollaText>
            </View>
            <TouchableOpacity
              onPress={() => {
                setIsCsvValid(true);
                setSelectedFile('');
              }}>
              <Image
                svg={true}
                width={20}
                height={20}
                style={{ width: 20, height: 20 }}
                source={require('@assets/img/icons/trash-grey.svg')}
              />
            </TouchableOpacity>
          </View>
        ) : (
          <>
            <SociollaText style={{ fontWeight: '600', marginBottom: 4 }}>
              Upload CSV file
            </SociollaText>
            <SociollaText
              style={{ color: '#888', fontSize: 12, marginBottom: 16 }}>
              CSV file, smaller than 1mb
            </SociollaText>
            <View style={{ width: '100%' }}>
              <Button
                title={
                  <SociollaText style={{ fontSize: 14, color: Colors.white }}>
                    Upload File
                  </SociollaText>
                }
                rounded={true}
                type="primary"
                style={{
                  alignItems: 'center',
                  width: '100%',
                }}
                onPress={pickFile}
                disabled={isLoading}
                disabledStyle={{ width: '100%' }}
              />
            </View>
          </>
        )}
      </View>
    );
  };

  const ModalContent = () => {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        onDrop>
        <View style={styles.container}>
          {isWebPlatform ? (
            <WebFileDropZone
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          ) : (
            <MobileFilePicker
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          )}

          {!isCsvValid ? (
            <SociollaText style={styles.invalidCsvText}>
              Please Upload Valid CSV
            </SociollaText>
          ) : (
            false
          )}

          <SociollaText style={styles.templateText}>
            Download CSV template{' '}
            <SociollaText
              style={styles.link}
              onPress={async () => {
                downloadCsvTemplate(props.templateUrl, props.name);
              }}>
              here
            </SociollaText>
          </SociollaText>

          <View
            style={{
              flexDirection: isWeb ? 'row' : 'column',
              width: '100%',
              justifyContent: 'space-evenly',
            }}>
            <Button
              testID={`${props.testID || 'modal-upload'}-cancel-btn`}
              title={
                <SociollaText style={{ fontSize: 14 }}>Cancel</SociollaText>
              }
              style={{ width: '100%' }}
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb ? 150 : '100%',
                backgroundColor: Colors.white,
                marginTop: 10,
              }}
              onPress={handleClose}
              disabled={isLoading}
              disabledStyle={{ width: isWeb ? 150 : '100%' }}
            />
            <Button
              testID={`${props.testID || 'modal-upload'}-proceed-btn`}
              title={
                <SociollaText style={{ fontSize: 14, color: Colors.white }}>
                  Proceed
                </SociollaText>
              }
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb ? 150 : '100%',
                marginTop: 10,
              }}
              onPress={handleUpload}
              disabled={isLoading}
              disabledStyle={{ width: isWeb ? 150 : '100%' }}
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <ModalFull
      testID={props.testID || 'modal-upload'}
      show={props.show}
      title="Import"
      width={isWeb ? 380 : '100%'}
      onClose={handleClose}>
      <ModalContent />
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 380,
    backgroundColor: Colors.white,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    elevation: 4,
  },
  templateText: {
    color: '#444',
    fontSize: 14,
    marginBottom: 24,
    alignSelf: 'flex-start',
  },
  invalidCsvText: {
    color: Colors.red0,
    fontSize: 14,
    marginBottom: 10,
    alignSelf: 'flex-start',
  },

  webDropZone: (isDragOver) => ({
    border: `2px dashed ${isDragOver ? Colors.grey10 : Colors.blue0}`,
    background: isDragOver ? Colors.grey3 : Colors.white,
    borderRadius: 12,
    padding: 20,
    width: '85%',
    textAlign: 'center',
    marginBottom: 24,
    cursor: 'pointer',
    fontFamily: 'figtree-regular',
  }),
  fileContainer: {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
    textAlign: 'start',
  },
  noFileContainer: {
    fontFamily: 'figtree-regular',
    width: '100%',
  },

  link: {
    color: Colors.blue0,
    textDecorationLine: 'underline',
  },

  mobileDropZone: {
    borderWidth: 2,
    borderColor: Colors.grey7,
    borderRadius: 12,
    padding: 40,
    alignItems: 'center',
    marginBottom: 24,
    width: '100%',
  },
  mobileFileContainer: {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
    textAlign: 'start',
  },
});

ModalUpload.propTypes = propTypes;

export default ModalUpload;
